import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, MapPin } from "lucide-react";
import { SITE, IMAGES } from "@/site";

const LINES = ["Wake up", "your fitness.", "Anywhere."];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.3 } },
};
const line = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section id="top" ref={ref} className="relative min-h-[92vh] md:min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="Mobile personal training in an Essex park" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#020617]/85" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/40 to-transparent" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-7xl w-full px-5 md:px-10 pb-16 md:pb-24 pt-28">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="flex items-center gap-2 mb-6"
        >
          <MapPin size={14} className="text-[#FACC15]" />
          <span className="font-mono-label text-[11px] text-[#94A3B8]">{SITE.area}</span>
        </motion.div>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          className="font-display font-bold text-[#F8FAFC] text-5xl sm:text-7xl lg:text-8xl xl:text-9xl"
        >
          {LINES.map((t, i) => (
            <span key={i} className="block overflow-hidden py-[0.05em]">
              <motion.span
                variants={line}
                className={`block ${i === 2 ? "text-[#FACC15]" : ""}`}
              >
                {t}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <p className="max-w-md text-[#94A3B8] text-base md:text-lg leading-relaxed">
            A premium mobile personal trainer who comes to your home, garage, garden, workplace
            or local park. No gym membership. No intimidation. Just your goals, your space.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-cta-book"
              className="font-mono-label text-[11px] bg-[#FACC15] text-[#020617] px-7 py-4 rounded-full font-semibold hover:bg-[#EAB308] transition-colors duration-300"
            >
              Start your journey
            </button>
            <button
              onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-cta-services"
              className="font-mono-label text-[11px] border border-white/25 text-[#F8FAFC] px-7 py-4 rounded-full hover:border-[#FACC15] hover:text-[#FACC15] transition-colors duration-300"
            >
              How it works
            </button>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 right-6 z-10 hidden md:flex items-center gap-2 text-[#94A3B8]"
      >
        <span className="font-mono-label text-[10px]">Scroll</span>
        <ArrowDown size={14} className="animate-bounce text-[#FACC15]" />
      </motion.div>
    </section>
  );
};
